import React from 'react';
import { useForm } from 'react-hook-form';
import { TextInput, Button } from 'playup-web-core';
import { useLocation, useHistory } from 'react-router-dom';
import styled from 'styled-components';

import Eft from './Eft';
import appConfig from '../../../appConfig';

import {
	addDepositRequestTypeToUser,
	deleteCreditcard,
	getDepositPromotions,
	getDepositRequestTypes,
	removeDepositRequestTypeFromUser,
} from '../../../api/users';
import DepositPromotions from './DepositPromotions';
import ReusableModal from '../../../components/ReusableModal/ReusableModal';
import AddAmountButton from './AddAmountButton';
import useAsyncAction from '../../../hooks/useAsyncAction';
import DepositRow from './DepositRow';
import { ConvertPaymentTypeToView, DepositPage, PaymentMethods, PaymentPages } from './DepositService';
import { parseApiError } from '../../../utils/parseApiError';
import { pendingBlueshyftCodes } from '../../../api/deposit';
import CashPaymentButtons from './Blueshyft/CashPaymentButtons';
import { BlueshyftCodePage } from './Blueshyft/BlueshyftCodePage';
import { BlueshyftCode } from './Blueshyft/BlueshyftCode';
import { PendingBlueshyftCodes } from './Blueshyft/PendingBlueshyftCodes';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import CashMap from './Blueshyft/CashMap';
import CreditCard from './CreditCard/CreditCard';

// SVG Imports
import CashupSvg from './assets/cashup.svg';
import EftSmall from './assets/eftsmall.svg';
import Mastercard from './assets/mastercard.svg';
import BpaySvg from './BPAY/Bpay-logo.svg';
import Visa from './assets/visa.svg';
import InfoBlock from '../../../components/InfoBlock';
import formatCurrency from '../../../utils/formatCurrency';
import Bpay from './BPAY/Bpay';
import { SavedPaymentTypes } from './SavedPaymentTypes';
import PayIDDetail from './PayIDDetail';
import PayidIcon from './PAYID/PayID_icon.png';
import useAppStatus from '../../../hooks/useAppStatus';
import { noop } from 'lodash';

// this is shocking atm. Clean up to look more like app
// TODO: this will need to be refactored after SignUp Flow redesign.

const DepositModal = (props: Props) => {
	const history = useHistory();
	const { user, refreshPortalUser } = React.useContext(PortalSessionContext);
	const location = useLocation();
	const [loading, errorMessage, performAction] = useAsyncAction();

	const [depositLimits, setDepositLimits] = React.useState<DepositLimits>({
		min_deposit: 100,
		max_deposit: 50000000,
		min_blueshyft_deposit: 1000,
		max_blueshyft_deposit: 800000,
	});
	const [activeDepositLimit, setActiveDepositLimit] = React.useState<DepositLimit>({
		min: depositLimits.min_deposit,
		max: depositLimits.max_deposit,
	});
	const [depositPromotionsState, setDepositPromotionsState] = React.useState<PromotionsDeposit>({ available: [] });
	const [currentBlueshyftCode, setCurrentBlueshyftCode] = React.useState<BlueshyftCode>();
	const [previousCodes, setPreviousCodes] = React.useState<PendingBlueshyftCodes[]>();
	const [warningMessage, setWarningMessage] = React.useState<string | null>(null);
	const [depositState, setDepositState] = React.useState<{
		error?: null | string;
		processing: boolean;
		done: boolean;
	}>({
		error: null,
		processing: false,
		done: false,
	});
	const [amountError, setAmountError] = React.useState<string>();
	const [userPaymentMethods, setUserPaymentMethods] = React.useState<UserResponsePaymentTypes[]>([]);
	const [availablePaymentMethods, setAvailablePaymentMethods] = React.useState<
		{
			id: number;
			name: string;
			isInstant: boolean;
		}[]
	>();
	const [currentPageIndex, setCurrentPageIndex] = React.useState<number>(0);
	const [selectedPaymentType, setSelectedPaymentType] = React.useState<UserResponsePaymentTypes>({
		user_id: null,
		deposit_request_type: null,
		card: null,
	});
	const [lastAddedType, setLastAddedType] = React.useState<number | null>(null);
	const [nextPress, setNextPress] = React.useState(false);

	const state = location.state as DepositState;
	const getDefaultAmount = () => {
		if (state?.defaultAmount !== undefined) {
			return state?.defaultAmount >= depositLimits.min_deposit
				? state?.defaultAmount / 100
				: depositLimits.min_deposit / 100;
		}
		return undefined;
	};
	const defaultAmount = getDefaultAmount();

	const { register, watch, setValue } = useForm<FormData>({
		defaultValues: {
			amount: defaultAmount,
		},
	});
	const { activeApp } = useAppStatus();
	const amount = watch('amount');

	React.useEffect(() => {
		const close = (event) => {
			if (event.key === 'Escape') props.onClose();
		};
		document.addEventListener('keydown', close);
		performAction(async () => {
			await updateUserPaymentTypes(true);
			const previousCodes = await pendingBlueshyftCodes();
			setPreviousCodes(previousCodes);
		});

		return () => {
			document.removeEventListener('keydown', close);
		};
	}, []);

	React.useEffect(() => {
		performAction(async () => {
			await updateUserPaymentTypes();
		});
	}, [user?.balance]);

	React.useEffect(() => {
		if ((defaultAmount as number) < activeDepositLimit.min / 100) {
			setWarningMessage(`You must deposit at least ${formatCurrency(activeDepositLimit.min)}`);
		} else if (state?.warningMessage) {
			setWarningMessage(state?.warningMessage);
		}
	}, [state?.warningMessage, defaultAmount, activeDepositLimit.min]);

	React.useEffect(() => {
		if (lastAddedType && lastAddedType !== PaymentMethods.Card) {
			performAction(async () => {
				await updateUserPaymentTypes();
				const newType = userPaymentMethods.find(
					(x) => x?.deposit_request_type?.id === lastAddedType
				) as UserResponsePaymentTypes;
				setSelectedPaymentType(newType);
				setLastAddedType(null);
			});
		} else {
			setLastAddedType(null);
		}
	}, [lastAddedType]);

	React.useEffect(() => {
		if (
			selectedPaymentType?.deposit_request_type?.id === PaymentMethods.Cash &&
			depositLimits.min_blueshyft_deposit &&
			depositLimits.max_blueshyft_deposit
		) {
			setActiveDepositLimit({ min: depositLimits.min_blueshyft_deposit, max: depositLimits.max_blueshyft_deposit });
		} else if (depositLimits.min_deposit && depositLimits.max_deposit) {
			setActiveDepositLimit({ min: depositLimits.min_deposit, max: depositLimits.max_deposit });
		}
	}, [depositLimits, selectedPaymentType]);

	React.useEffect(() => {
		const locationState = history?.location?.state as { from: string };
		if (locationState?.from !== 'registration') {
			return;
		}

		const intervalId = setInterval(() => {
			getPromotions();
		}, 5000);
		return () => clearInterval(intervalId);
	}, [history?.location?.state]);

	const updateUserPaymentTypes = async (firstRun?: boolean) => {
		const depositRequestTypes = await getDepositRequestTypes();

		if (depositRequestTypes.meta.min_deposit && depositRequestTypes.meta.max_deposit) {
			setDepositLimits({
				min_deposit: depositRequestTypes.meta.min_deposit,
				max_deposit: depositRequestTypes.meta.max_deposit,
				min_blueshyft_deposit: depositRequestTypes.meta.min_blueshyft_deposit,
				max_blueshyft_deposit: depositRequestTypes.meta.max_blueshyft_deposit,
			});
			setActiveDepositLimit({
				min: depositRequestTypes.meta.min_deposit,
				max: depositRequestTypes.meta.max_deposit,
			});
		}

		getPromotions();
		const previouslyUsedPaymentMethods = depositRequestTypes.data;
		const keyAvailablePaymentMethods: { id: number; name: string; isInstant: boolean }[] = [];
		Object.keys(depositRequestTypes.meta.deposit_request_types).forEach((key) => {
			keyAvailablePaymentMethods.push({
				id: parseInt(key),
				name: depositRequestTypes.meta.deposit_request_types[key],
				isInstant: checkWhetherPaymentMethodIsInstant(parseInt(key)),
			});
		});

		setUserPaymentMethods(
			previouslyUsedPaymentMethods.filter((x) => {
				// remove here disabled payment methods (if any)
				return true;
			})
		);

		setAvailablePaymentMethods(
			keyAvailablePaymentMethods.filter((x) => {
				// remove disabled payment methods
				if (x.id === PaymentMethods['Apple Pay'] || x.id === PaymentMethods['Google Pay']) return false;

				return true;
			})
		);
		if (previouslyUsedPaymentMethods.length > 0 && firstRun)
			setSelectedPaymentType({ ...previouslyUsedPaymentMethods[0] });
	};

	// This is temporary. BE should tell us whether the method is instant. If this will stay in FE, we should move it to DepositService
	const checkWhetherPaymentMethodIsInstant = (paymentMethodId: number) => {
		if (
			paymentMethodId === PaymentMethods.Card ||
			paymentMethodId === PaymentMethods.Cash ||
			paymentMethodId === PaymentMethods.PayID
		) {
			return true;
		}
		return false;
	};

	const removePaymentTypeFromList = async (paymentType: UserResponsePaymentTypes) => {
		// need to add additional buttons here
		const paymentId = paymentType?.deposit_request_type?.id as number;
		if (paymentId !== PaymentMethods.Card) {
			await removeDepositRequestTypeFromUser(paymentId);
			setUserPaymentMethods([
				...userPaymentMethods?.filter((paymentMethod) => paymentMethod?.deposit_request_type?.id !== paymentId),
			]);
			return;
		}
		deleteCreditcard(paymentType.card?.id as number)
			.catch((error) => {
				setDepositState({
					...depositState,
					error: parseApiError(error.errors),
				});
			})
			.finally(() => {
				refreshPortalUser();
				updateUserPaymentTypes();
			});
	};

	const addNewPaymentMethod = async (paymentType: { id: number; name: string }) => {
		if (paymentType.id === PaymentMethods.Card) {
			setLastAddedType(paymentType.id);
			setCurrentPageIndex(PaymentPages.NewCard);
		} else {
			await addDepositRequestTypeToUser(paymentType.id);
			await updateUserPaymentTypes();
			setLastAddedType(paymentType.id);
			setCurrentPageIndex(PaymentPages.Home);
		}
	};

	const getPromotions = () => {
		getDepositPromotions().then((promotionsResponse) => {
			if (promotionsResponse.data) {
				setDepositPromotionsState((oldPromotionsState) => {
					const sortedPromotions = promotionsResponse.data.sort((a, b) => {
						const targetName = activeApp.name === 'PlayUp' ? 'wagering' : 'Draftstars';
						if (a.client.name === targetName) return -1;
						if (b.client.name === targetName) return 1;
						return 0;
					});
					// if no promotions were available and now they are automatically select the first one:
					const findSuitablePromotion = sortedPromotions.find((x) => {
						if (activeApp.name === 'PlayUp') {
							return x.client.name === 'wagering';
						} else {
							return x.client.name === 'Draftstars';
						}
					});
					if (sortedPromotions.length > 0 && oldPromotionsState.available.length === 0)
						return {
							available: sortedPromotions,
							selected: findSuitablePromotion
								? {
										id: findSuitablePromotion.id,
										client_id: findSuitablePromotion.client.id,
								  }
								: {
										id: sortedPromotions[0].id,
										client_id: sortedPromotions[0].client.id,
								  },
						};
					// if previous promotion selected
					if (
						sortedPromotions.length > 0 &&
						oldPromotionsState?.selected &&
						sortedPromotions.some((x) => x.id === oldPromotionsState?.selected?.id)
					)
						return {
							available: sortedPromotions,
							selected:
								oldPromotionsState.selected && findSuitablePromotion
									? {
											id: findSuitablePromotion.id,
											client_id: findSuitablePromotion.client.id,
									  }
									: {
											...oldPromotionsState.selected,
									  },
						};

					return { available: sortedPromotions };
				});
			}
		});
	};

	React.useEffect(() => {
		if (amount < activeDepositLimit.min / 100)
			setAmountError(`Minimum deposit of: ${formatCurrency(activeDepositLimit.min)}`);
		else if (amount > activeDepositLimit.max / 100)
			setAmountError(`Maximum deposit of: ${formatCurrency(activeDepositLimit.max)}`);
		else setAmountError(undefined);
	}, [activeDepositLimit, amount]);

	const selectPromotion = (id: string) => {
		const promotion = depositPromotionsState.available.find((promotion) => promotion.id === id);

		setDepositPromotionsState({
			...depositPromotionsState,
			selected:
				!promotion || depositPromotionsState.selected?.id === id
					? undefined
					: {
							id,
							client_id: promotion.client.id,
					  },
		});
	};

	const onAddAmountButton = (addAmount: number) => {
		if (amount) setValue('amount', amount + addAmount);
		else setValue('amount', addAmount);
	};

	// This component cannot be moved out because we register amount here, and that needs to be passed into all subcomponents of this parent
	const DepositAmount = (isCC = false, handleCC = noop) => {
		const disabled = isCC ? !amount : !amount || !selectedPaymentType?.user_id;
		const onDepositClick = (event) => {
			event.stopPropagation();
			// handle our different deposit types]
			if (disabled) {
				if (!selectedPaymentType?.user_id) setWarningMessage('You must add or select a payment type');
				else if (!amount || amount < activeDepositLimit.min / 100)
					setWarningMessage(`You must deposit at least ${formatCurrency(activeDepositLimit.min)}`);
				else if (amount > activeDepositLimit.max / 100)
					setWarningMessage(`You must deposit at most ${formatCurrency(activeDepositLimit.max)}`);
				return;
			}
			if (isCC) {
				handleCC();
				return;
			}

			if (selectedPaymentType.deposit_request_type?.id === PaymentMethods.Card) {
				setCurrentPageIndex(PaymentPages.Card);
			} else if (selectedPaymentType.deposit_request_type?.id === PaymentMethods.Eft) {
				setCurrentPageIndex(PaymentPages.Eft);
			} else if (selectedPaymentType.deposit_request_type?.id === PaymentMethods.Bpay) {
				setCurrentPageIndex(PaymentPages.BPay);
			}
		};

		return (
			<DepositAmountContainer>
				{!isCC && <DepositBreak />}
				<TextInput
					kind="white"
					className="_pb-6 _w-full"
					autoComplete="off"
					label="Deposit Amount"
					type="number"
					autoFocus
					prefix="$"
					placeholder={`Minimum ${formatCurrency(activeDepositLimit.min)}`}
					{...register('amount', {
						valueAsNumber: true,
						min: {
							value: activeDepositLimit.min / 100,
							message: `The minimum deposit amount is ${formatCurrency(activeDepositLimit.min)}`,
						},
					})}
					error={amountError}
				/>
				<DepositPromotions
					depositPromotions={depositPromotionsState.available}
					selectedPromotionId={depositPromotionsState.selected?.id}
					selectPromotion={selectPromotion}
				/>
				<AddDepositButtonsContainer>
					<AddAmountButton additionalAmount={10} onClick={onAddAmountButton} />
					<AddAmountButton additionalAmount={25} onClick={onAddAmountButton} />
					<AddAmountButton additionalAmount={50} onClick={onAddAmountButton} />
					<AddAmountButton additionalAmount={100} onClick={onAddAmountButton} />
					<AddAmountButton additionalAmount={500} onClick={onAddAmountButton} />
				</AddDepositButtonsContainer>
				{selectedPaymentType?.deposit_request_type?.id === PaymentMethods.Cash && !isCC ? (
					<CashPaymentButtons
						amount={amount}
						updatePage={setCurrentPageIndex}
						selectedPromotion={depositPromotionsState.selected}
						setError={setAmountError}
						setCode={setCurrentBlueshyftCode}
					/>
				) : (
					<>
						<StyledButton kind="white" type="submit" height={50} isLoading={loading} onClick={onDepositClick}>
							{isCC ? 'Next' : 'Deposit now'}
						</StyledButton>
						{isCC && (
							<StyledButton kind="danger" height={50} onClick={() => setCurrentPageIndex(PaymentPages.Home)}>
								Cancel
							</StyledButton>
						)}
					</>
				)}
				{warningMessage && <DepositWarning>{warningMessage}</DepositWarning>}
			</DepositAmountContainer>
		);
	};

	const viewBlueshyftCodes = () => {
		setCurrentPageIndex(PaymentPages.Cash);
	};

	const closeCC = () => {
		updateUserPaymentTypes();
		setCurrentPageIndex(PaymentPages.Home);
	};

	const currentPromotion = React.useMemo(() => {
		return depositPromotionsState.available.find((x) => x.id === depositPromotionsState.selected?.id);
	}, [depositPromotionsState]);

	// this needs to be moved to its own component
	const CreditCardAmount = () => {
		return (
			<CreditCardAmountContainer>
				{!nextPress && (
					<>
						{DepositAmount(true, () => setNextPress(true))};
						<div style={{ height: 70 }} />
						<InfoBlock text="Once your first deposit has been processed successfully, your Credit/Debit Card details will be securely stored." />
					</>
				)}

				{nextPress && (
					<CreditCard
						setAmountError={setAmountError}
						userUpdater={refreshPortalUser}
						selectedPromotion={currentPromotion}
						amount={amount}
						close={closeCC}
						onTryAgain={closeCC}
					/>
				)}
			</CreditCardAmountContainer>
		);
	};

	const HomePageBody = () => {
		return (
			<>
				<SavedPaymentTypes
					viewBlueshyftCodes={viewBlueshyftCodes}
					previousCodes={previousCodes}
					setCurrentPageIndex={setCurrentPageIndex}
					removePaymentTypeFromList={removePaymentTypeFromList}
					selectedPaymentType={selectedPaymentType}
					userPaymentMethods={userPaymentMethods}
					setSelectedPaymentType={setSelectedPaymentType}
				/>
				{DepositAmount()}
			</>
		);
	};
	const HomePage: DepositPage = {
		pageIndex: PaymentPages.Home,
		subTitle: undefined,
		previousPageIndex: undefined,
		body: HomePageBody,
	};

	const availableInstantPaymentMethods = availablePaymentMethods?.filter((p) => p.isInstant);
	const availableNonInstantPaymentMethods = availablePaymentMethods?.filter((p) => !p.isInstant);
	const NewPaymentSelections = () => {
		return (
			<>
				{availableInstantPaymentMethods && availableInstantPaymentMethods.length > 0 && (
					<>
						<PaymentMethodTimingContainer>
							<PaymentMethodTimingText>Instant Deposit Options</PaymentMethodTimingText>
						</PaymentMethodTimingContainer>
						{availableInstantPaymentMethods?.map((paymentType: { id: number; name: string }, index: number) => {
							// filter used methods - card
							const paymentView = ConvertPaymentTypeToView(paymentType);
							return (
								<DepositRow
									key={paymentType?.id}
									title={paymentView?.name}
									image={paymentView?.icon as any}
									type="paymentType"
									selected={selectedPaymentType?.deposit_request_type?.id === paymentView?.id}
									onClick={() => {
										addNewPaymentMethod(paymentType);
									}}
									isOnTop={index === 0}
								/>
							);
						})}
					</>
				)}
				{availableNonInstantPaymentMethods && availableNonInstantPaymentMethods.length > 0 && (
					<>
						<PaymentMethodTimingContainer>
							<PaymentMethodTimingText>1-3 Days Deposit Options</PaymentMethodTimingText>
						</PaymentMethodTimingContainer>
						{availableNonInstantPaymentMethods?.map((paymentType: { id: number; name: string }, index: number) => {
							// filter used methods - card
							const paymentView = ConvertPaymentTypeToView(paymentType);
							return (
								<DepositRow
									key={paymentType?.id}
									title={paymentView?.name}
									image={paymentView?.icon as any}
									type="paymentType"
									selected={selectedPaymentType?.deposit_request_type?.id === paymentView?.id}
									onClick={() => {
										addNewPaymentMethod(paymentType);
									}}
									isOnTop={index === 0}
								/>
							);
						})}
					</>
				)}
			</>
		);
	};

	const SelectPage: DepositPage = {
		pageIndex: PaymentPages.Select,
		subTitle: 'Select Deposit Method',
		previousPageIndex: PaymentPages.Home,
		body: <NewPaymentSelections />,
	};

	const NewCreditCardPage: DepositPage = {
		pageIndex: PaymentPages.NewCard,
		subTitle: 'Deposit with Credit Card',
		previousPageIndex: PaymentPages.Select,
		body: CreditCardAmount(),
	};

	const CreditCardPage: DepositPage = {
		pageIndex: PaymentPages.Card,
		subTitle: 'Deposit with Credit Card',
		previousPageIndex: PaymentPages.Home,
		body: (
			<CreditCard
				setAmountError={setAmountError}
				userUpdater={refreshPortalUser}
				selectedPromotion={currentPromotion}
				amount={amount}
				close={closeCC}
				onTryAgain={closeCC}
				savedCreditCard={
					selectedPaymentType?.card ? user?.user_cards?.find((x) => x.id === selectedPaymentType?.card?.id) : undefined
				}
			/>
		),
	};

	const EftPage: DepositPage = {
		pageIndex: PaymentPages.Eft,
		subTitle: 'Pay with EFT',
		previousPageIndex: PaymentPages.Home,
		body: <Eft amount={amount} setAmountError={setAmountError} selectedPromotion={depositPromotionsState.selected} />,
	};

	const BlueshyftPage: DepositPage = {
		pageIndex: PaymentPages.Cash,
		subTitle: 'Pay with Cash',
		previousPageIndex: PaymentPages.Home,
		body: (
			<BlueshyftCodePage
				code={currentBlueshyftCode}
				previousCodes={previousCodes}
				setPreviousCodes={setPreviousCodes}
				pendingBlueshyftCodes={pendingBlueshyftCodes}
				setCurrentPageIndex={setCurrentPageIndex}
			/>
		),
	};

	const MapPage: DepositPage = {
		pageIndex: PaymentPages.Map,
		previousPageIndex: PaymentPages.Home,
		subTitle: 'Cash In-store',
		body: <CashMap />,
	};

	const BPayPage: DepositPage = {
		pageIndex: PaymentPages.BPay,
		previousPageIndex: PaymentPages.Home,
		subTitle: 'BPAY',
		body: <Bpay amount={amount} bpayDetails={user?.bpay_details} selectedPromotion={depositPromotionsState.selected} />,
	};

	const pages = [HomePage, SelectPage, NewCreditCardPage, CreditCardPage, EftPage, BlueshyftPage, MapPage, BPayPage];
	const getPage = (pageIndexToFind: number) => {
		return pages.find((x) => x.pageIndex === pageIndexToFind);
	};
	const currentPage = getPage(currentPageIndex);

	return (
		<ReusableModal
			title="Deposit"
			onClose={props.onClose}
			onBack={
				currentPage?.previousPageIndex !== undefined
					? () => {
							setCurrentPageIndex(currentPage.previousPageIndex as number);
					  }
					: undefined
			}
			subTitle={currentPage?.subTitle ? pages[currentPageIndex].subTitle : undefined}
		>
			{currentPage?.pageIndex === PaymentPages.Home ? (
				<>
					<SavedPaymentTypes
						viewBlueshyftCodes={viewBlueshyftCodes}
						previousCodes={previousCodes}
						setCurrentPageIndex={setCurrentPageIndex}
						removePaymentTypeFromList={removePaymentTypeFromList}
						selectedPaymentType={selectedPaymentType}
						userPaymentMethods={userPaymentMethods}
						setSelectedPaymentType={setSelectedPaymentType}
					/>
					{selectedPaymentType?.deposit_request_type?.id === PaymentMethods.PayID ? <PayIDDetail /> : DepositAmount()}
				</>
			) : (
				currentPage?.body
			)}
			{/* this disgusting ternary is used to deal with react hook form register */}
		</ReusableModal>
	);
};

export default DepositModal;

interface FormData {
	amount: number;
}

type DepositState = {
	defaultAmount: number;
	warningMessage: string;
};

type DepositLimit = {
	min: number;
	max: number;
};

interface Props {
	onClose: () => void;
	showRG?: boolean;
}
interface PromotionsDeposit {
	available: Array<DepositPromotion>;
	selected?: { id: string; client_id: number };
}

export const getSvgIcon = (paymentType: UserResponsePaymentTypes) => {
	if (paymentType?.deposit_request_type?.id === PaymentMethods.PayID) return PayidIcon;
	if (paymentType?.deposit_request_type?.id === PaymentMethods.Cash) return CashupSvg;
	if (paymentType?.deposit_request_type?.id === PaymentMethods.Eft) return EftSmall;
	if (paymentType?.deposit_request_type?.id === PaymentMethods.Bpay) return BpaySvg;
	if (paymentType?.deposit_request_type?.id === PaymentMethods.Card) {
		const firstNumber = paymentType.card?.label?.charAt(0);
		if (firstNumber === '4') return Visa;
		if (firstNumber === '5') return Mastercard;
	} else return EftSmall;
};

export const AddNew = styled.button`
	height: 32px;
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: row;
	padding: 20px;
	width: 100%;
	margin-top: 10px;
	margin-bottom: 10px;
	color: #00549e;
	height: 16px;
	font-weight: 500;
	border: 1px solid #00549e;
	border-radius: 5px;
`;

export const DepositAddBreak = styled.div`
	width: auto;
	height: 1px;
	background-color: #dde1e3;
`;

const DepositBreak = styled.div`
	width: auto;
	box-sizing: border-box;
	height: 6px;
	background-color: #dde1e3;
	// this is a hack to break padding rules and should be addressed. Don't do this at home.
	margin-left: -24px;
	margin-right: -24px;
	margin-bottom: 22px;
`;

const DepositAmountContainer = styled.div`
	label {
		font-size: 14px;
		color: #4a5568;
	}
	margin-bottom: auto;
`;

const AddDepositButtonsContainer = styled.div`
	margin-top: 30px;
	display: inline-flex;
	justify-content: space-between;
	width: 100%;
`;

const PaymentMethodTimingContainer = styled.div`
	display: flex;
	align-items: center;
	width: calc(100% + 48px);
	height: 40px;
	margin-left: -24px;
	margin-right: -24px;
	background-color: #dde1e3;
`;

const PaymentMethodTimingText = styled.p`
	color: #191d22;
	margin: 0;
	margin-left: 20px;
	font-weight: 600;
`;

const DepositWarning = styled.div`
	background-color: #e6e6e6;
	color: #000;
	height: auto;
	font-size: 13px;
	text-align: center;
	line-height: 14px;
	margin-top: 15px;
	border-radius: 4px;
	padding: 2px;
`;

export const NoMethods = styled.div`
	height: 42px;
	color: #dde1e3;
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: row;
`;

const CreditCardAmountContainer = styled.div`
	margin-top: 20px;
`;

const StyledButton = styled(Button)`
	margin: 20px 0;
`;
